import React, { useState, useCallback, useEffect } from 'react';
import { adminService } from '../api/services';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Search, MapPin, AlertCircle, UserX, UserCheck, TrendingUp, X, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Suspend Modal ────────────────────────────────────────────────
const SuspendModal = ({ client, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const name = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();

  const handle = async () => {
    setLoading(true);
    try { await onConfirm(client.id, reason); onClose(); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="font-serif text-xl text-forest-900">Suspend Account</h3>
        <p className="text-sm text-forest-600">Suspend <strong>{name}</strong>? They will be logged out immediately and notified via SMS.</p>
        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Reason <span className="text-forest-400">(optional — sent to user)</span></label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="Account suspended due to..."
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
        </div>
        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">Cancel</button>
          <button onClick={handle} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 text-sm py-2.5 rounded-full bg-rust-500 text-white hover:bg-rust-600 transition-colors font-medium disabled:opacity-60">
            {loading ? 'Suspending...' : <><UserX size={14} /> Suspend</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Promote to Expert Modal ──────────────────────────────────────
const PromoteModal = ({ client, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    bio: '',
    specializations: '',
    yearsOfExperience: '',
    qualifications: '',
    linkedinUrl: '',
    adminNotes: '',
  });
  const name = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handle = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        yearsOfExperience: form.yearsOfExperience ? parseInt(form.yearsOfExperience, 10) : 0,
      };
      await onConfirm(client.id, payload);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-5 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl text-forest-900">Promote to Expert</h3>
            <p className="text-sm text-forest-500 mt-0.5">
              Upgrade <strong>{name}</strong>'s account to Expert. They will be pre-approved and can immediately access the expert dashboard.
            </p>
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 transition-colors ml-4 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* What they'll gain */}
        <div className="bg-forest-50 border border-forest-100 rounded-xl px-4 py-3 text-sm text-forest-700 space-y-1">
          <p className="font-semibold text-forest-800 mb-2">Upon promotion they will be able to:</p>
          {[
            'Access the Expert Dashboard',
            'Create & host 1-on-1 live sessions',
            'Receive bookings from clients',
            'Review & grade canvas submissions',
            'Track earnings and payment history',
            'Host and join group sessions as an expert',
          ].map(item => (
            <div key={item} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-forest-500 flex-shrink-0" />
              {item}
            </div>
          ))}
          <p className="text-xs text-forest-500 mt-2 pt-2 border-t border-forest-100">
            Their existing client profile and learning progress will be preserved. They must log in again to get an updated session token.
          </p>
        </div>

        {/* Optional profile fields */}
        <p className="text-xs font-semibold text-forest-500 uppercase tracking-wider">Expert Profile Details <span className="font-normal normal-case">(all optional — can be filled by expert later)</span></p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Bio</label>
            <textarea value={form.bio} onChange={set('bio')} rows={2}
              placeholder="Brief professional background..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1">Specializations</label>
              <input type="text" value={form.specializations} onChange={set('specializations')}
                placeholder="e.g. Tourism, Hospitality"
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1">Years of Experience</label>
              <input type="number" min="0" max="60" value={form.yearsOfExperience} onChange={set('yearsOfExperience')}
                placeholder="0"
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Qualifications</label>
            <input type="text" value={form.qualifications} onChange={set('qualifications')}
              placeholder="e.g. BSc Tourism Management"
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">LinkedIn URL</label>
            <input type="url" value={form.linkedinUrl} onChange={set('linkedinUrl')}
              placeholder="https://linkedin.com/in/..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Admin Notes</label>
            <textarea value={form.adminNotes} onChange={set('adminNotes')} rows={2}
              placeholder="Internal note about this promotion..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">
            Cancel
          </button>
          <button onClick={handle} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 text-sm py-2.5 rounded-full bg-forest-700 text-white hover:bg-forest-800 transition-colors font-medium disabled:opacity-60">
            {loading ? 'Promoting...' : <><TrendingUp size={14} /> Promote to Expert</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Delete Modal ─────────────────────────────────────────────────
const DeleteModal = ({ client, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const name = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || client.email;

  const handle = async () => {
    setLoading(true);
    try {
      await onConfirm(client.id, name);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full bg-rust-50 flex items-center justify-center text-rust-500 mb-2">
          <Trash2 size={24} />
        </div>
        <h3 className="font-serif text-xl text-forest-900">Delete Student / Client</h3>
        <p className="text-sm text-forest-600 leading-relaxed">
          Are you sure you want to permanently delete <strong>{name}</strong>? This action cannot be undone. All their learning records, cohort memberships, and account data will be removed.
        </p>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} disabled={loading}
            className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">
            Cancel
          </button>
          <button onClick={handle} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 text-sm py-2.5 rounded-full bg-rust-500 text-white hover:bg-rust-600 transition-colors font-medium disabled:opacity-60">
            {loading ? 'Deleting...' : <><Trash2 size={14} /> Delete Account</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Actions Dropdown ─────────────────────────────────────────────
const ActionsMenu = ({ client, onSuspend, onReactivate, onPromote, onDelete }) => {
  const [open, setOpen] = useState(false);
  const name = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="p-2 text-forest-300 hover:text-forest-600 hover:bg-forest-50 rounded-lg transition-colors"
        title="Actions">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
          <circle cx="7.5" cy="2.5" r="1.25" fill="currentColor"/>
          <circle cx="7.5" cy="7.5" r="1.25" fill="currentColor"/>
          <circle cx="7.5" cy="12.5" r="1.25" fill="currentColor"/>
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-cream-200 rounded-xl shadow-lg w-48 py-1 text-sm overflow-hidden">

            {/* Promote — only if ACTIVE client */}
            {client.status === 'ACTIVE' && (
              <button
                onClick={() => { setOpen(false); onPromote(client); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-forest-700 hover:bg-forest-50 transition-colors text-left">
                <TrendingUp size={13} className="text-forest-500" />
                Promote to Expert
              </button>
            )}

            {/* Suspend / Reactivate */}
            {client.status === 'ACTIVE' ? (
              <button
                onClick={() => { setOpen(false); onSuspend(client); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-rust-500 hover:bg-rust-50 transition-colors text-left">
                <UserX size={13} /> Suspend Account
              </button>
            ) : client.status === 'SUSPENDED' ? (
              <button
                onClick={() => { setOpen(false); onReactivate(client.id, name); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-forest-700 hover:bg-forest-50 transition-colors text-left">
                <UserCheck size={13} /> Reactivate Account
              </button>
            ) : null}

            {/* Delete Account */}
            <div className="border-t border-cream-100 my-1" />
            <button
              onClick={() => { setOpen(false); onDelete(client); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-rust-500 hover:bg-rust-50 transition-colors text-left font-medium">
              <Trash2 size={13} /> Delete Account
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────
const AdminClientsPage = () => {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const LIMIT = 25;

  const params = { page, limit: LIMIT };
  if (appliedSearch) params.search = appliedSearch;

  const [clients,    setClients]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await adminService.getAllClients(params);
      const body = res.data;
      // ApiResponse.paginated: { data: [...], meta: { total, totalPages } }
      const rows = Array.isArray(body.data) ? body.data : [];
      setClients(rows);
      setTotal(body.meta?.total ?? rows.length);
      setTotalPages(body.meta?.totalPages ?? 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load clients');
    } finally { setLoading(false); }
  }, [appliedSearch, page]); // eslint-disable-line

  useEffect(() => { refetch(); }, [refetch]);

  const handleSuspend = async (id, reason) => {
    try {
      await adminService.suspendUser(id, reason);
      toast.success('Account suspended. User has been logged out and notified.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not suspend account');
      throw err;
    }
  };

  const handleReactivate = async (id, name) => {
    if (!window.confirm(`Reactivate ${name}'s account?`)) return;
    try {
      await adminService.reactivateUser(id);
      toast.success('Account reactivated. User has been notified.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reactivate account');
    }
  };

  const handlePromote = async (id, profileData) => {
    try {
      await adminService.promoteClientToExpert(id, profileData);
      toast.success('User promoted to Expert! They will need to log in again to access the expert dashboard.');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not promote user');
      throw err;
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await adminService.deleteClient(id);
      toast.success(`Student "${name}" deleted successfully.`);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete student account');
      throw err;
    }
  };

  const statusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-forest-50 text-forest-700 border-forest-200',
      SUSPENDED: 'bg-rust-50 text-rust-500 border-rust-200',
      INACTIVE: 'bg-cream-100 text-forest-400 border-cream-200',
    };
    return <span className={`text-[10px] font-medium border px-2.5 py-0.5 rounded-full ${styles[status] ?? ''}`}>{status}</span>;
  };

  return (
    <DashboardLayout title="Client Management">
      {suspendTarget && (
        <SuspendModal client={suspendTarget} onClose={() => setSuspendTarget(null)} onConfirm={handleSuspend} />
      )}
      {promoteTarget && (
        <PromoteModal client={promoteTarget} onClose={() => setPromoteTarget(null)} onConfirm={handlePromote} />
      )}
      {deleteTarget && (
        <DeleteModal client={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}

      <div className="space-y-5 max-w-5xl">

        {/* Search */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
            <input type="text" placeholder="Search by name or email..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (setAppliedSearch(search), setPage(1))}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white" />
          </div>
          <button onClick={() => { setAppliedSearch(search); setPage(1); }} className="btn-primary text-sm py-2.5 px-5">Search</button>
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load clients.</span>
          </div>
        )}

        <p className="text-sm text-forest-500">{loading ? 'Loading...' : ''}</p>

        <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-cream-50 border-b border-cream-100 text-xs font-semibold text-forest-500 uppercase tracking-widest">
            <div className="col-span-4">Client</div>
            <div className="col-span-3 hidden sm:block">Business</div>
            <div className="col-span-2 hidden md:block">Level</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="space-y-0 divide-y divide-cream-100">
              {[1,2,3,4,5].map(i => <div key={i} className="px-5 py-4 animate-pulse h-14 bg-white" />)}
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16 text-forest-400">
              <p className="font-serif text-xl text-forest-600">No clients found</p>
            </div>
          ) : (
            <div className="divide-y divide-cream-50">
              {clients.map(client => {
                const name = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();
                const biz = client.clientProfile?.businessName;
                const level = client.clientProfile?.currentLevel?.name;
                return (
                  <div key={client.id} className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-cream-50 transition-colors">
                    <div className="col-span-4 flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 font-serif text-xs font-bold flex-shrink-0">
                        {name[0]}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-forest-900 truncate">{name}</div>
                        <div className="text-xs text-forest-400 truncate">{client.email}</div>
                      </div>
                    </div>
                    <div className="col-span-3 hidden sm:block">
                      <div className="text-sm text-forest-700 truncate">{biz ?? '—'}</div>
                      {client.clientProfile?.city && (
                        <div className="text-xs text-forest-400 flex items-center gap-1 mt-0.5">
                          <MapPin size={9} /> {client.clientProfile.city}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 hidden md:block text-xs text-forest-600 truncate">{level ?? '—'}</div>
                    <div className="col-span-2">{statusBadge(client.status)}</div>
                    <div className="col-span-1 flex justify-end">
                      <ActionsMenu
                        client={client}
                        onSuspend={setSuspendTarget}
                        onReactivate={handleReactivate}
                        onPromote={setPromoteTarget}
                        onDelete={setDeleteTarget}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-3 py-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            ← Prev
          </button>
          <span className="text-sm text-forest-500">
            Page {page} of {totalPages || 1} · {total} clients
          </span>
          <button
            disabled={page >= (totalPages || 1)}
            onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Next →
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminClientsPage;